import { useExperimentStore, useLanguageStore, useUserStore, useRoutingStore } from '@/helpers/composables/useStores';
import { computed, ComputedRef, inject, ref } from 'vue';
import { injectLocal, MaybeRefOrGetter, provideLocal, toValue, useMounted } from '@vueuse/core';
import { ExperimentTrackEventOptions } from '@/components/experiments/Core/types';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/user.store';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import { Offer } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ClickoutOfferDetails } from '@/interfaces/snowplow/clickout-context';
import InternationalOffers, {
	InternationalOffersExpName,
	InternationalOffersExpVariants,
} from '@/components/experiments/InternationalOffers';
const experimentSymbol = Symbol('InternarionalOffersExp');

export type TrackSingleQtvExp = ReturnType<typeof useInternationalOffers>['trackInternationalOffers'];

const tagsByVariant = {
	[InternationalOffersExpVariants.CONTROL]: 'amazon-us-intoff-co-20',
	[InternationalOffersExpVariants.VARIANT_1]: 'amazon-us-intoff-v1-20',
};

export const useInternationalOffers = (isExpActiveCondition?: ComputedRef<boolean>) => {
	const { activeVariantsWithControlGroup } = useExperimentStore();
	const { country } = useLanguageStore();
	const { newBuyboxView } = useUserStore();
	const { activeRoute } = useRoutingStore();
	const isMounted = useMounted();

	if (isExpActiveCondition) {
		provideLocal(experimentSymbol, isExpActiveCondition);
	}

	const isExpActive = injectLocal<ComputedRef<boolean>>(
		experimentSymbol,
		computed(() => false)
	);

	const rowCountry = injectLocal<ComputedRef<string | null>>(
		'row-country',
		computed(() => null)
	);

	const expActiveVariant = computed(() => {
		if (
			!isMounted.value ||
			!isExpActive.value ||
			newBuyboxView.value !== NewBuyBoxView.LIST ||
			activeRoute.value.name !== 'app.detail.movies'
		) {
			return undefined;
		}
		return activeVariantsWithControlGroup.value[InternationalOffersExpName] as
			| InternationalOffersExpVariants
			| undefined;
	});

	function trackInternationalOffers(TrackingOptions: ExperimentTrackEventOptions<InternationalOffersExpVariants>) {
		if (expActiveVariant.value) {
			InternationalOffers.trackEvent({
				property: 'international-offers',
				...TrackingOptions,
			});
		}
	}

	const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
	const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));

	const { clickoutType, placement } = getClickoutTrackingPlacement('qtv');
	const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);

	function getClickoutUrl(_offer: MaybeRefOrGetter<Offer | (Offer & ClickoutOfferDetails)>, title: TitleDetail) {
		const offer = toValue(_offer);
		const offerEntityId = episodeEntityId ?? title.id;
		const uct_ct = expActiveVariant.value ? tagsByVariant[expActiveVariant.value] : undefined;
		const clickoutUrl = new ClickoutUrl(offer, { skipYieldOptimization: true, isFreeTrial: true })
			.addContexts(titleContext.value)
			.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
			.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
			.updateContext('ClickoutContextGraphql', 'placement', placement)
			.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
			.setTitleSourceId(title.id)
			.set('uct_country', country.value)
			.set('uct_buybox', BuyboxParam.Normal)
			.updateContext('TitleContextGraphql', 'offerEntityId', offerEntityId);
		// .skipUCT2();

		if (uct_ct) {
			const url = new URL(clickoutUrl.get('r')!);
			clickoutUrl.set('uct_ct', 'fallback');
			url.searchParams.set('tag', uct_ct);
			clickoutUrl.set('r', url.toString()!.replace('app.primevideo.com', 'www.primevideo.com'));
		}

		return clickoutUrl;
	}

	return {
		trackInternationalOffers,
		expActiveVariant,
		getClickoutUrl,
		rowCountry,
	};
};
